import { Injectable } from '@angular/core';
import { AdministratorFacade, getRolesAndPermission } from '@piqet-angular/data';
import {
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  rolesModuleName,
  subModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ColDef } from 'ag-grid-community';
import { AdministratorService } from 'libs/data/src/lib/services/administrator/administrator.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductPackagingRatioDataGridService {
  productPackagingPayload: any;
  event: any;
  editProductPackagingRatioData = new Subject<[any, any, any]>();
  productPackagingRatioEditObservable$ = this.editProductPackagingRatioData.asObservable();
  productPackagingRatioContentData = new Subject<[any, any]>();
  productPackagingRatioDeleteObservable$ = this.productPackagingRatioContentData.asObservable();

  constructor(
    private swalService: SwalService,
    private administratorService: AdministratorService,
    private administratorFacade: AdministratorFacade
  ) {}

  getProductPackagingColumn(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'SectorsIdentifierCode',
      headerName: 'Sector code',
      field: 'sectorsIdentifierCode',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
      cellStyle: { 'text-decoration': 'underline' },
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          const checkAccess = this.isAccess() ? this.isAccess().isEdit : true;
          checkAccess &&
            this.editProductPackagingRatioData.next([
              params.data.sectorsIdentifierId,
              params.data.regionId,
              'Review product package ratio detail',
            ]);
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'SectorsIdentifierName',
      headerName: 'Sector identifier',
      field: 'sectorsIdentifierName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'RegionCode',
      headerName: 'Region code',
      field: 'regionCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'RegionName',
      headerName: 'Region',
      field: 'regionName',
      cellClass: 'my-permission-cursor-pointer',
      minWidth: grid_widths_map.sm_column_sm,
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRendererParams: {
        showEdit: this.isAccess() ? this.isAccess().isEdit : true,
        showDelete: this.isAccess() ? this.isAccess().isDelete : true,
        value: 'adminProductPackageRatio',
        editData: (data: any, pathName: any) => {
          this.editProductPackagingRatioData.next([
            data.sectorsIdentifierId,
            data.regionId,
            'Review product package ratio detail',
          ]);
        },
        deleteData: (data: any, pathName: any) => {
          this.deleteRecycledContent(data.sectorsIdentifierId, data.regionId);
        },
      },
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  isAccess = () => {
    return getRolesAndPermission(
      rolesModuleName.DataManageProductPackageRatio,
      subModuleName.EOLDataModule
    );
  };

  deleteRecycledContent(sectorsIdentifierId: any, regionId: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.productPackagingRatioContentData.next([sectorsIdentifierId, regionId]);
      } else {
      }
    });
  }

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.getProductPackagingColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.productPackagingPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'sectorsIdentifierCode',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Asc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }
}
